/**
 * Página de listagem de produtos para pedido
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Box, Fab, Badge, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ShoppingCart, ViewModule, ViewList } from '@mui/icons-material';
import { Layout, Loading, ErrorMessage, SearchBar, EmptyState, PageHeader } from '@components';
import { ProdutoCard } from '@components/produto/ProdutoCard';
import { ProdutoCardItem } from '@components/produto/ProdutoCardItem';
import { CarrinhoProvider, useCarrinhoContext } from '@contexts';
import { useSessionContext } from '@contexts';
import { useFetch, useFilter, useDebounce } from '@hooks';
import { produtoController, classificacaoController } from '@controllers';
import { Inventory } from '@mui/icons-material';
import { ROUTES } from '@types';
import { PainelPedido } from '@components';
import theme from '@theme';

type ViewMode = 'grid' | 'list';
type OrderBy = 'nome-asc' | 'nome-desc' | 'preco-asc' | 'preco-desc';

const ListaProdutoPedidoContent: React.FC = () => {
  const { idClassificacao } = useParams<{ idClassificacao: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list'); // Começa em modo lista (compacto)
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { cliente } = useSessionContext();
  const { addItem, updateQuantity, updatePrice, getItemQuantity, getQuantidadeEstoqueLoja, isUsandoEstoqueLoja, totalItems, carrinho } =
    useCarrinhoContext();

  // Redireciona para seleção de cliente se não houver cliente
  useEffect(() => {
    if (!cliente) {
      navigate(ROUTES.CLIENTES);
    }
  }, [cliente, navigate]);

  // Carrega classificação
  const { data: classificacao } = useFetch(
    () =>
      idClassificacao
        ? classificacaoController.obterPorCodigo(idClassificacao)
        : Promise.resolve(null),
    { key: idClassificacao }
  );

  // Carrega produtos
  const { data: produtos, isLoading, error, refetch } = useFetch(
    () =>
      idClassificacao
        ? produtoController.obterProdutoPrevisaoPedido(idClassificacao, debouncedSearch)
        : Promise.resolve([]),
    { key: `${idClassificacao}-${debouncedSearch}` }
  );

  // Filtra produtos
  const { filteredData } = useFilter(produtos || [], ['nome', 'codigo']);

  // Ordena produtos e remove duplicatas
  const sortedData = React.useMemo(() => {
    if (!filteredData) return [];

    // Remove duplicatas baseado no código do produto
    const uniqueProducts = Array.from(
      new Map(filteredData.map(produto => [produto.codigo, produto])).values()
    );

    const [field, direction] = orderBy.split('-') as ['nome' | 'preco', 'asc' | 'desc'];

    if (field === 'nome') {
      return produtoController.ordenarPorNome(uniqueProducts, direction);
    } else {
      return produtoController.ordenarPorPreco(uniqueProducts, direction);
    }
  }, [filteredData, orderBy]);

  const handleUpdateQuantidade = (produto: any, quantidade: number) => {
    if (quantidade === 0) {
      updateQuantity(produto.codigo, 0);
    } else if (getItemQuantity(produto.codigo) === 0) {
      addItem(produto, quantidade);
    } else {
      updateQuantity(produto.codigo, quantidade);
    }
  };

  const handleUpdatePreco = (produto: any, novoPreco: number) => {
    try {
      const quantidade = getItemQuantity(produto.codigo);

      // Se o produto não está no carrinho, adiciona com quantidade 1
      if (quantidade === 0) {
        addItem(produto, 1);
      }

      // Atualiza o preço
      updatePrice(produto.codigo, novoPreco);
    } catch (error: any) {
      console.error('Erro ao atualizar preço:', error);
      // O componente CampoPrecoEditavel já trata o erro visualmente
    }
  };

  const getPrecoEditado = (codigoProduto: string): number | undefined => {
    const item = carrinho.items.get(codigoProduto);
    return item?.precoUnitario;
  };

  if (!cliente) {
    return null; // Redireciona via useEffect
  }

  if (isLoading) {
    return <Loading message="Carregando produtos..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Erro ao carregar produtos"
        message={error.message}
        onRetry={refetch}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={classificacao?.nome || 'Produtos'}
        subtitle={`Cliente: ${cliente.cliNome} • ${sortedData.length} produtos`}
        showBackButton
        onBack={() => navigate('/classificacoes')}
      />

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: { xs: '1 1 60%', sm: 1 }, order: { xs: 1, sm: 1 } }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar produto..."
          />
        </Box>

        <FormControl sx={{ minWidth: 200, display: { xs: 'none', md: 'flex' }, order: { sm: 2 } }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="preco-asc">Menor preço</MenuItem>
            <MenuItem value="preco-desc">Maior preço</MenuItem>
          </Select>
        </FormControl>

        {/* Toggle de visualização */}
        <Box sx={{ display: 'flex', gap: 0.5, order: { xs: 2, sm: 3 } }}>
          <Tooltip title="Visualização em Grade">
            <IconButton
              color={viewMode === 'grid' ? 'primary' : 'default'}
              onClick={() => setViewMode('grid')}
              size="large"
            >
              <ViewModule />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visualização em Lista">
            <IconButton
              color={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
              size="large"
            >
              <ViewList />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {sortedData.length === 0 ? (
        <EmptyState
          title="Nenhum produto encontrado"
          message={
            searchTerm ? 'Tente ajustar sua busca' : 'Não há produtos nesta categoria'
          }
          icon={<Inventory />}
        />
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {sortedData.map((produto) => {
            const disponivel = produtoController.calcularDisponibilidade(produto);
            const quantidade = getItemQuantity(produto.codigo);
            const precoEditado = getPrecoEditado(produto.codigo);
            const qtdEstoqueLoja = getQuantidadeEstoqueLoja(produto.codigo);
            const usandoEstoqueLoja = isUsandoEstoqueLoja(produto.codigo);

            // Calcula estoque loja disponível (subtrai quantidade já no carrinho)
            const estoqueLojaDisponivel = Number(produto.estoque_loja) - qtdEstoqueLoja;

            // Cria objeto produto atualizado com estoque loja disponível
            const produtoComEstoqueAtualizado = {
              ...produto,
              estoque_loja: Math.max(0, estoqueLojaDisponivel)
            };

            return (
              <Grid item xs={6} sm={6} md={3} key={produto.codigo}>
                <ProdutoCard
                  produto={produtoComEstoqueAtualizado}
                  tipo="pedido"
                  quantidade={quantidade}
                  precoEditado={precoEditado}
                  disponivel={disponivel}
                  estoque={produto.estoque}
                  usandoEstoqueLoja={usandoEstoqueLoja}
                  onUpdateQuantidade={(qtd) => handleUpdateQuantidade(produto, qtd)}
                  onUpdatePreco={(preco) => handleUpdatePreco(produto, preco)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box>
          {sortedData.map((produto) => {
            const disponivel = produtoController.calcularDisponibilidade(produto);
            const quantidade = getItemQuantity(produto.codigo);
            const precoEditado = getPrecoEditado(produto.codigo);
            const qtdEstoqueLoja = getQuantidadeEstoqueLoja(produto.codigo);
            const usandoEstoqueLoja = isUsandoEstoqueLoja(produto.codigo);

            // Calcula estoque loja disponível (subtrai quantidade já no carrinho)
            const estoqueLojaDisponivel = Number(produto.estoque_loja) - qtdEstoqueLoja;

            // Cria objeto produto atualizado com estoque loja disponível
            const produtoComEstoqueAtualizado = {
              ...produto,
              estoque_loja: Math.max(0, estoqueLojaDisponivel)
            };

            return (
              <ProdutoCardItem
                key={produto.codigo}
                produto={produtoComEstoqueAtualizado}
                tipo="pedido"
                quantidade={quantidade}
                precoEditado={precoEditado}
                disponivel={disponivel}
                estoque={produto.estoque}
                usandoEstoqueLoja={usandoEstoqueLoja}
                onUpdateQuantidade={(qtd) => handleUpdateQuantidade(produto, qtd)}
                onUpdatePreco={(preco) => handleUpdatePreco(produto, preco)}
              />
            );
          })}
        </Box>
      )}

      {/* FAB do Carrinho */}
      {totalItems > 0 && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            display: 'none'
          }}
          onClick={() => navigate('/carrinho')}
        >
          <Badge badgeContent={totalItems} color="error">
            <ShoppingCart />
          </Badge>
        </Fab>
      )}
    </>
  );
};

export const ListaProdutoPedidoPage: React.FC = () => {
  return (
    <Layout title="Pedido" showHeader showFooter footerType="pedido" showPainelPedido>
      <PainelPedido />
      <CarrinhoProvider tipo="pedido">
        <ListaProdutoPedidoContent />
      </CarrinhoProvider>
    </Layout>
  );
};

export default ListaProdutoPedidoPage;